import { Button, Col, Text } from '@opendoor/bricks-next';
import type { BoxProps } from '@opendoor/bricks-next';
import type { EntryComponent } from 'cms/entries/entries';
import type { IFcMap } from 'declarations/contentful';

import { FCContentContainer, FCContentContainerWrapper } from './shared/FCLayout';
import Marker, { MarkerPulseClassName } from './shared/Marker';
import { resizeMapBackgroundImageUrl, resizeMapMarkerImageUrl } from './shared/utils';

const MAP_BACKGROUND_IMAGE_URL =
  'https://images.opendoor.com/source/s3/imgdrop-production/2026-02-1770659423528-20363.png?service=cosmos';
const MAP_MARKER_IMAGE_URLS = {
  leftTop:
    'https://images.opendoor.com/source/s3/imgdrop-production/2023-04-1682635416264-57298.png?service=cosmos',
  rightTop:
    'https://images.opendoor.com/source/s3/imgdrop-production/2023-04-1682635416269-85419.png?preset=square-2048',
  leftMiddle:
    'https://images.opendoor.com/source/s3/imgdrop-production/2023-04-1682635416260-76025.png?preset=square-2048',
  leftBottom:
    'https://images.opendoor.com/source/s3/imgdrop-production/2023-04-1682635416240-88834.png?service=cosmos',
  rightBottom:
    'https://images.opendoor.com/source/s3/imgdrop-production/2023-04-1682635416253-95450.png?preset=square-2048',
} as const;

const PulsingMarker = ({
  $largerThanSM,
  ...restProps
}: Pick<BoxProps, 'left' | 'right' | 'top' | 'display' | '$largerThanXS' | '$largerThanSM'> & {
  imageSource: string;
  animationDelay?: string;
}) => (
  <Marker
    height="$40x"
    width="$101x"
    $largerThanSM={{ height: '$51x', width: '$129x', ...$largerThanSM }}
    className={MarkerPulseClassName}
    {...restProps}
  />
);

const RenderFCMap = (entry: IFcMap) => {
  const { fields } = entry;

  return (
    <FCContentContainerWrapper
      position="relative"
      width="100%"
      overflow="hidden"
      style={{
        backgroundColor: '#051a44',
        backgroundImage: `url(${resizeMapBackgroundImageUrl(MAP_BACKGROUND_IMAGE_URL)})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
      minHeight="$382x"
      $largerThanSM={{ minHeight: '$616x' }}
    >
      <PulsingMarker
        imageSource={resizeMapMarkerImageUrl(MAP_MARKER_IMAGE_URLS.leftTop)}
        left="10%"
        top="17%"
        $largerThanXS={{ top: '12%' }}
        $largerThanSM={{ top: '12%' }}
        animationDelay="2s"
      />
      <PulsingMarker
        imageSource={resizeMapMarkerImageUrl(MAP_MARKER_IMAGE_URLS.rightTop)}
        right="7%"
        top="4%"
        $largerThanSM={{ right: '30%', top: '7%' }}
        animationDelay="0.5s"
      />
      <PulsingMarker
        imageSource={resizeMapMarkerImageUrl(MAP_MARKER_IMAGE_URLS.leftMiddle)}
        display="none"
        $largerThanSM={{ display: 'flex' }}
        left="8%"
        top="40%"
        animationDelay="0.5s"
      />
      <PulsingMarker
        imageSource={resizeMapMarkerImageUrl(MAP_MARKER_IMAGE_URLS.leftBottom)}
        left="15%"
        top="80%"
        $largerThanSM={{ top: '85%' }}
      />
      <PulsingMarker
        imageSource={resizeMapMarkerImageUrl(MAP_MARKER_IMAGE_URLS.rightBottom)}
        right="4%"
        top="90%"
        $largerThanXS={{ top: '85%' }}
        $largerThanSM={{ right: '15%', top: '60%' }}
        animationDelay="2.5s"
      />
      <FCContentContainer>
        <Col justifyContent="center" alignItems="center" flex={1} gap="$12x">
          <Col gap="$12x" alignItems="center">
            <Text
              tag="h2"
              typography="$headerMedium"
              textAlign="center"
              color="$contentInversePrimary"
              $largerThanSM={{ typography: '$displaySmall', fontSize: 60 }}
            >
              {fields.title}
            </Text>
            <Text
              tag="p"
              typography="$labelXlarge"
              fontWeight="$medium"
              textAlign="center"
              color="$contentInversePrimary"
              marginTop="$4x"
              maxWidth="$616x"
            >
              {fields.subtitle}
            </Text>
          </Col>
          <Button
            variant="primary"
            size="small"
            label={fields.ctaButtonText}
            analyticsName="cosmos-fc-map-cta"
            href={fields.ctaUrl}
            marginTop="$14x"
            $largerThanSM={{ size: 'large' }}
          />
        </Col>
      </FCContentContainer>
    </FCContentContainerWrapper>
  );
};

const FCMap: EntryComponent<IFcMap> = {
  render: RenderFCMap,
};

export default FCMap;
